import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookie from "js-cookie";
import useCart from "store";
import dayjs from "dayjs";

// components
import Layout from "layout/index";
import { SvgIconStyle, CommonInput } from "components";
import { Typography, Box, Button, Snackbar } from "@mui/material";

// asset
import DefaultStandBy from "assets/image/default_standby.jpeg";
import BackIcon from "assets/icons/back.svg";

// ----------------------------------------------------------------------
const CALENDAR_FORMAT = "YYYY-MM-DD";

export default function AdminSettingPage() {
	const navigate = useNavigate();
	const KIOSK_ID = Cookie.get("Kiosk_ID");
	const { festival } = useCart((state) => state);

	const [dateArr, setDateArr] = useState([]);
	const [changeState, setChangeState] = useState(false);
	const [currentDate, setCurrentDate] = useState(
		Cookie.get("qs_kiosk_date")
			? dayjs(Cookie.get("qs_kiosk_date")).format(CALENDAR_FORMAT)
			: dayjs().format(CALENDAR_FORMAT)
	);
	const [changeDate, setChangeDate] = useState(currentDate);
	const [snackbarState, setSnackbarState] = useState({ open: false, text: "" }); // 토스트알림 온오프

	// 날짜변경 초기화
	const handleReset = () => {
		setChangeState(false);
		setChangeDate(null);
		Cookie.remove("qs_kiosk_date");
		setCurrentDate(dayjs().format(CALENDAR_FORMAT));
	};

	// 날짜변경 API - 당일 자정까지 유지
	const handleChange = () => {
		// 오늘 자정 시간 계산
		const midnight = new Date();
		midnight.setHours(23, 59, 59, 999);

		Cookie.set("qs_kiosk_date", changeDate ?? dayjs().format(CALENDAR_FORMAT), {
			expires: midnight,
		});
		setCurrentDate(changeDate ?? dayjs().format(CALENDAR_FORMAT));
	};

	useEffect(() => {
		const startDate = dayjs(festival.startDate);
		const endDate = dayjs(festival.endDate);
		let current = startDate;
		const arr = [];

		while (current <= endDate) {
			arr.push(current.format(CALENDAR_FORMAT));
			current = current.add(1, "day");
		}

		setDateArr(arr);
	}, [festival]);
	return (
		<Layout sx={{ px: 6, pt: 4.5, overflowY: "scroll" }}>
		<Box sx={{ display: "flex", justifyContent:'space-between',alignItems: "center", }}>
				<Box sx={{ display: "flex", gap: 4 }}>
				<Box onClick={() => navigate("/")} sx={{ mt: 1 }}>
					<SvgIconStyle src={BackIcon} origin size={20} />
				</Box>
				<Typography variant="h2">관리자 페이지</Typography>
				</Box>
				<Typography variant="h1" color={'primary.main'} >{ KIOSK_ID}</Typography>
			</Box>

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 6,
					p: 6,
				}}
			>
				<Box
					sx={{
						width: 326,
						height: 404,
						borderRadius: 1,
						border: "1px solid gray",
						background: `url(${
							festival?.thumbnail === "" ? DefaultStandBy : festival.thumbnail
						}) no-repeat center / cover`,
					}}
				/>

				<Typography variant="h2" textAlign={"center"}>
					{festival?.branchName}
				</Typography>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						width: "100%",
					}}
				>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Typography variant="h5">ID</Typography>
						<Typography variant="subtitle2" textAlign={"right"}>
							{festival?.branchId}
						</Typography>
					</Box>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Typography variant="h5">등록기간</Typography>
						<Typography variant="subtitle2" textAlign={"right"}>
							{`${dayjs(festival?.startDate).format(
								"YYYY.MM.DD"
							)} ~ ${dayjs(festival?.endDate).format("MM.DD")}`}
						</Typography>
					</Box>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Typography variant="h5">현재 적용일</Typography>

						<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
							<Button
								variant="contained"
								sx={{ px: 3 }}
								onClick={() => handleReset()}
							>
								<Typography variant="subtitle1">초기화</Typography>
							</Button>
							<Button
								variant="outlined"
								sx={{ px: 3 }}
								onClick={() => setChangeState(true)}
							>
								<Typography variant="subtitle1">변경</Typography>
							</Button>
							<Typography variant="subtitle2" textAlign={"right"}>
								{dayjs(currentDate).format("YYYY.MM.DD")}
							</Typography>
						</Box>
					</Box>
				</Box>

				{changeState ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 4,
							width: "100%",
						}}
					>
						<Box
							sx={{
								display: " grid",
								gridTemplateColumns: " repeat(7, 1fr)",
								gap: 1,
							}}
						>
							{dateArr.map((date) => (
								<Button
									key={date}
									variant={changeDate === date ? "contained" : "outlined"}
									color={changeDate === date ? "primary" : "secondary"}
									sx={{ color: changeDate === date ? "white" : "black" }}
									onClick={() => {
										changeDate === date
											? setChangeDate(null)
											: setChangeDate(date);
									}}
								>
									<Typography variant="body2">
										{dayjs(date).format("MM.DD")}
									</Typography>
								</Button>
							))}
						</Box>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 2,
							}}
						>
							<Button
								variant="contained"
								color="secondary"
								sx={{
									py: 1,
									width: "100%",
									color: "black",
									bgcolor: "grey.400",
									"&:hover,&:active": { color: "black", bgcolor: "grey.400" },
								}}
								onClick={() => setChangeState(false)}
							>
								<Typography variant="subtitle1">돌아가기</Typography>
							</Button>
							<Button
								variant="contained"
								sx={{ py: 1, width: "100%" }}
								onClick={() => handleChange()}
							>
								<Typography variant="subtitle1">적용하기</Typography>
							</Button>
						</Box>
					</Box>
				) : null}
			</Box>

			{/* toast */}
			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={snackbarState.open}
				message={snackbarState.text}
			/>
			{/* toast */}
		</Layout>
	);
}
